import { DEFAULT_WINDOWING } from "../../constants/windowPresets";
/**
 * HangingProtocolGrid - Main 2x2 Grid Container for CT Hanging Protocol
 *
 * Orchestrates the 4-viewport layout for CT studies:
 * +------------------+------------------+
 * | Top-Left         | Top-Right        |
 * | Axial Pre-Con    | Axial Post-Con   |
 * +------------------+------------------+
 * | Bottom-Left      | Bottom-Right     |
 * | Coronal Post-Con | Sagittal Post-Con|
 * +------------------+------------------+
 *
 * Uses atomFamily pattern for per-slot state isolation.
 * Each slot has its own atom - no cross-slot interference.
 */

import React, { useEffect, useLayoutEffect, useCallback, useMemo, useRef, useState } from "react";
import { useAtomValue, useSetAtom, useAtom, useStore } from "jotai";
import { ProtocolViewport } from "./ProtocolViewport";
import { resolveGridReslice } from "./resolveGridReslice";
import { SegmentationViewerPanel } from "./SegmentationViewerPanel";
import { CompanionPlaneToggle } from "./CompanionPlaneToggle";
import { MeasureSegmentationViewerPanel } from "./MeasureSegmentationViewerPanel";
import { measureSegmentationResultAtom } from "../../atoms/measureAtoms";
import { FirstTimeHelpToast } from "./FirstTimeHelpToast";
import { StudyMetadataResponse, shouldAutoInvert } from "../../utils/dicomMetadataLoader";
import { warnOnMixedSignedTransferSyntax } from "../../utils/signCorrectionTelemetry";
import {
  assignSeriesToSlots,
  SLOT_IDS,
  SlotId,
  getSlotConfigs,
  DEFAULT_SLOT,
  hasHangingProtocolSupport,
  getDominantAcquisitionGroup,
  checkAcquisitionGroupMismatch,
  getAcquisitionGroup,
} from "../../utils/hangingProtocolService";
import {
  slotStateFamily,
  activeSlotAtom,
  maximizedSlotAtom,
  createDefaultSlotState,
  toggleInvertAtom,
  resetSlotViewAtom,
  scrollSliceAtom,
  layoutModeAtom,
  companionPlaneAtom,
  type CompanionPlane,
  referenceLinesEnabledAtom,
  setPrioritySeriesAtom,
  prioritySeriesIdAtom,
  volumeStatusFamily,
} from "../../atoms/hangingProtocolAtoms";
import { buildReformatSlotState } from "./buildReformatSlotState";
import { resolveReformatTarget } from "./resolveReformatTarget";
import type { PlaneOrientation } from "../../viewport/types";
import { focusModeAtom } from "../../atoms/uiAtoms";
import { availableDiscriminatorsAtom, projectGatingModeAtom } from "../../atoms/gatingAtoms";
import {
  activeSegmentIndexAtom,
  isSegmentationActiveAtom,
  nnSegmentationResultAtom,
  savedAnnotationsAtom,
  segmentationVolumeIdAtom,
  seriesSegReadyFamily,
} from "../../atoms/segmentationAtoms";
import { on as busOn, off as busOff, emit as busEmit } from "../../lib/viewerBus";
import { GATING_MODE } from "../../types/gating";
import { PhaseSummaryPanel } from "./PhaseSummaryPanel";
import {
  setGetSiblingViewportsCallback,
  setNnSegmentationResultCallback,
} from "../../tools/RegionGrowTool";
import { dropSessionForPlane } from "../../tools/nnInteractiveSession";
import type { SiblingViewports, PlaneKey } from "../../tools/nnInteractiveSession";
import { PLANE_COLORS_HEX } from "../../constants/planeColors";
import { runCommand } from "../../lib/commands";
import { SHARED_RENDERING_ENGINE_ID } from "../../utils/renderingEngine";
import { useReferenceLinesSync } from "../../hooks/useReferenceLinesSync";
import {
  disposeAndRemoveVolume,
  getCornerstone,
  getCornerstoneTools,
  initializeCornerstone,
  purgeForeignStudyImages,
} from "../../utils/dicomLoader";
import {
  ensureContourSegmentationActive,
  getContourSegmentationId,
} from "../../utils/viewportToolSetup";
import { useHangingProtocolSync } from "../../hooks/useHangingProtocolSync";
import { perfMark, perfMeasure } from "../../utils/performanceTiming";
import {
  resizeAndRestoreViewports,
  selectOnScreenViewports,
  type ResizableViewport,
} from "../../utils/viewportResize";
import { logger } from "@/utils/logger";
import { ViewportWorkspace, createProductionWorkspaceDeps } from "../../viewport/ViewportWorkspace";
import { ViewportWorkspaceContext } from "../viewport/ViewportWorkspaceContext";
import {
  computeVolumeLru,
  volumeIdFor,
  volumeBaseFromId,
  foreignStudyVolumeIds,
} from "./volumeLru";
import { nextVisibleSlot } from "./slotCycle";

export interface HangingProtocolGridProps {
  studyId: string;
  studyMetadata: StudyMetadataResponse;
  /** Series ID that should load first (typically has findings/predictions) */
  prioritySeriesId?: string | null;
  /** Segmentation project uses multi-plane layout + 3D viewer in bottom-right */
  isSegmentationProject?: boolean;
  /** Only the active anatomy labeling route requests blinded pixels. */
  blindAnatomyTask?: boolean;
  /** Keep all four image panes instead of replacing bottom-right with stats/3D. */
  showSegmentationPanel?: boolean;
  /** Ignore the persisted stone 2-up mode and retain the standard 2×2 grid. */
  forceStandardLayout?: boolean;
  /**
   * Opt-in for renal-stone labeling projects (us3d-*): exclude non-kidney
   * anatomy from auto-assignment (brain, chest, extremity, …). See
   * `isLikelyNonKidneyAnatomy` in hangingProtocolService.
   */
  filterNonKidneyAnatomy?: boolean;
}

// Volume-cache LRU cap. Sized for 4 visible slots + 2 ping-pong spares.
// At ~400 MB/volume that's ~2.4 GB resident — fits under the 3 GB image-cache
// cap (computeCacheSizeBytes) with headroom for V8 + React.
const VOLUME_LRU_CAP = 6;

// How long the acquisition-mismatch warning toast stays up. Longer than the
// default 3.5s dismiss: the revert/undo action needs spotting + reaction time
// (matches the reporter's bulk-delete Undo toast).
const ACQUISITION_TOAST_DURATION_MS = 6000;

// Compile-time-safe map from the companion-pane setting to its plane key
// (avoids a `.toLowerCase() as PlaneKey` cast across two disjoint unions).
const COMPANION_TO_PLANE_KEY: Record<CompanionPlane, PlaneKey> = {
  Coronal: "coronal",
  Sagittal: "sagittal",
};

/**
 * Decide whether the bottom-right slot should render `SegmentationViewerPanel`
 * (the 3D model). Phase-dependent projects move the 3D model out of the grid
 * when ≥4 phases are available, except during an active annotation when it
 * temporarily reclaims the slot to keep volumetric feedback visible.
 *
 * Side effect for 4-phase studies: when annotation starts, the 4th phase's
 * viewport is hidden behind the seg panel until annotation ends. The series
 * stays loaded — its slot keeps the seriesId — but the user can't see it
 * during the draw. Intentional; the 3D feedback is more valuable than a
 * fourth comparison view mid-annotation.
 */
function renderBottomRightAsSegPanel(
  gatingMode: string,
  phaseCount: number,
  isAnnotationActive: boolean,
): boolean {
  if (gatingMode !== GATING_MODE.PHASE_DEPENDENT) return true;
  if (isAnnotationActive) return true;
  return phaseCount <= 3;
}

export const HangingProtocolGrid: React.FC<HangingProtocolGridProps> = ({
  studyId,
  studyMetadata,
  prioritySeriesId,
  isSegmentationProject = false,
  blindAnatomyTask = false,
  showSegmentationPanel = true,
  forceStandardLayout = false,
  filterNonKidneyAnatomy = false,
}) => {
  // Reporter measure flow uses the bottom-right slot for its 3D viewer.
  // Active when a measure session has resolved with contours; cleared on
  // Done/Cancel via useMeasureClickWiring.
  const measureSegResult = useAtomValue(measureSegmentationResultAtom);
  const showMeasure3D = !blindAnatomyTask && !!measureSegResult;

  // Slice + pan/zoom synchronizers across the 3 phase axial slots.
  // Membership tracks ELEMENT_ENABLED/DISABLED so late-mounting viewports
  // join automatically; toggles live in the Contrast Phase box. Study metadata
  // supplies per-slice world positions so slice sync links by anatomy, not by
  // index — and identically for stack and volume viewports.
  useHangingProtocolSync(studyMetadata);
  const defaultViewLoadRef = useRef<{
    studyId: string;
    markName: string;
    expectedSeries: Set<string>;
    completedSeries: Set<string>;
    totalSlices: number;
    completedSlices: number;
  } | null>(null);
  // Grid container ref — used by the drag-split handles further down. The
  // coordinated-resize effect uses a callback ref (`gridCallbackRef`) instead
  // so it rebinds when the slot grid actually mounts (the `!isSupported`
  // fallback at the bottom of this component renders a different div, and a
  // useEffect keyed on a stable jotaiStore would not rerun on the swap).
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [gridEl, setGridEl] = useState<HTMLDivElement | null>(null);
  const gridCallbackRef = useCallback((el: HTMLDivElement | null) => {
    gridRef.current = el;
    setGridEl(el);
  }, []);
  // Direct access to per-slot atoms
  const setSlotTopLeft = useSetAtom(slotStateFamily("top-left"));
  const setSlotTopRight = useSetAtom(slotStateFamily("top-right"));
  const setSlotBottomLeft = useSetAtom(slotStateFamily("bottom-left"));
  const setSlotBottomRight = useSetAtom(slotStateFamily("bottom-right"));

  // Read slot states for rendering
  const slotTopLeft = useAtomValue(slotStateFamily("top-left"));
  const slotTopRight = useAtomValue(slotStateFamily("top-right"));
  const slotBottomLeft = useAtomValue(slotStateFamily("bottom-left"));
  const slotBottomRight = useAtomValue(slotStateFamily("bottom-right"));

  // Shared coordination atoms
  const [activeSlot, setActiveSlot] = useAtom(activeSlotAtom);
  const [maximizedSlot, setMaximizedSlot] = useAtom(maximizedSlotAtom);
  const focusMode = useAtomValue(focusModeAtom);
  const setFocusMode = useSetAtom(focusModeAtom);
  const layoutMode = useAtomValue(layoutModeAtom);
  const setLayoutMode = useSetAtom(layoutModeAtom);
  const gatingMode = useAtomValue(projectGatingModeAtom);
  const [companionPlane, setCompanionPlane] = useAtom(companionPlaneAtom);
  // "seg-2up" (large axial + ONE companion + 3D) only makes sense for
  // view-dependent (stone) projects. Guard so a stale persisted "seg-2up" can't
  // leak into classification/phase layouts.
  const isViewDependent = gatingMode === GATING_MODE.VIEW_DEPENDENT;
  const effectiveLayoutMode = forceStandardLayout
    ? "2x2"
    : layoutMode === "seg-2up" && !isViewDependent
      ? "2x2"
      : layoutMode;
  const isSeg2Up = effectiveLayoutMode === "seg-2up";
  // The companion pane shows ONE of coronal/sagittal; the other plane's slot is
  // simply not mounted. We RELOCATE the mounted companion slot into the
  // top-right position (rather than swapping a slot's series) so each plane's
  // slot↔plane identity stays intact for nnInteractive sessions + the
  // plane-keyed cross-viewport slice sync.
  const companionSlotId: SlotId = companionPlane === "Sagittal" ? "bottom-left" : "top-right";
  const hiddenCompanionSlotId: SlotId = companionPlane === "Sagittal" ? "top-right" : "bottom-left";
  // Default view-dependent (stone) segmentation to the fast 2-view layout on
  // project entry. Switchable afterward within the session; re-applies when a
  // new view-dependent project mounts (gatingMode dep).
  useEffect(() => {
    if (forceStandardLayout) {
      setLayoutMode("2x2");
    } else if (gatingMode === GATING_MODE.VIEW_DEPENDENT) {
      setLayoutMode("seg-2up");
    }
  }, [forceStandardLayout, gatingMode, setLayoutMode]);
  // When the seg-2up companion switches, the plane we just left is unmounted.
  // Drop its now-stale nnInteractive session (its viewport is gone) so later
  // session iteration can't touch a destroyed viewport; its committed contours
  // remain rendered on canvas. Only acts in seg-2up.
  const prevCompanionRef = useRef(companionPlane);
  useEffect(() => {
    if (prevCompanionRef.current === companionPlane) return;
    const leftPlane: PlaneKey = companionPlane === "Sagittal" ? "coronal" : "sagittal";
    prevCompanionRef.current = companionPlane;
    if (isSeg2Up) dropSessionForPlane(leftPlane);
  }, [companionPlane, isSeg2Up]);
  const availableDiscriminators = useAtomValue(availableDiscriminatorsAtom);
  const isAnnotationActive = useAtomValue(isSegmentationActiveAtom);
  const phaseCount = useMemo(
    () => availableDiscriminators.filter((d) => d.kind === "phase").length,
    [availableDiscriminators],
  );
  const [referenceLinesEnabled, setReferenceLinesEnabled] = useAtom(referenceLinesEnabledAtom);
  const toggleInvert = useSetAtom(toggleInvertAtom);
  const resetSlotView = useSetAtom(resetSlotViewAtom);
  const scrollSlice = useSetAtom(scrollSliceAtom);
  useReferenceLinesSync();

  // Cor/sag reformat (MPR) decision. A pane reslices ONLY where the user asked
  // for one — `reslice` on that slot, set by the series right-click menu.
  // `resolveGridReslice` keeps the unified path on by default for every
  // project regardless of whether a reformat was requested (reslice renders
  // only there, but that's no longer why it's on) and honors `?newViewport=0`.
  const newViewportParam = useMemo(
    () => (blindAnatomyTask ? "1" : new URLSearchParams(window.location.search).get("newViewport")),
    [blindAnatomyTask],
  );
  // Project only the field the resolver reads. Whole slot objects churn
  // on every scroll/W-L/zoom tick (new reference each time), and `reslice`
  // (derived below) is passed as a ProtocolViewport prop that must stay
  // referentially stable across unrelated slot-state ticks.
  const slotStates = useMemo(
    () => ({
      "top-left": { reslice: slotTopLeft.reslice },
      "top-right": { reslice: slotTopRight.reslice },
      "bottom-left": { reslice: slotBottomLeft.reslice },
      "bottom-right": { reslice: slotBottomRight.reslice },
    }),
    [slotTopLeft.reslice, slotTopRight.reslice, slotBottomLeft.reslice, slotBottomRight.reslice],
  );
  const { unified: resliceUnified, resliceBySlot } = useMemo(
    () => resolveGridReslice({ slotStates, isSegmentationProject, newViewportParam }),
    [slotStates, isSegmentationProject, newViewportParam],
  );

  // Unified viewport path (#575) — on by default for every project now.
  // `resolveGridReslice` already resolved it (folding in `?newViewport`), so
  // this single flag drives BOTH the shared workspace and every per-pane
  // ProtocolViewport (via `unifiedPath`) — they can never disagree. When off
  // (only via `?newViewport=0`), no workspace is created and the legacy
  // per-pane path behaves as before.
  const newViewportFlag = resliceUnified;
  // The single layout-scoped workspace OBJECT, or null when the flag is off.
  // Created LAZILY DURING THE FIRST RENDER (not in an effect) so descendant
  // SessionVolumeMounts see it in context on their very first render. An
  // effect-created workspace is null on the children's first mount (child
  // effects run before the parent's), which forced a fragile async-timing race
  // + a per-pane fallback that re-created the duplicate-listener bug this
  // consolidation exists to kill.
  // C1 cold-load fix: do NOT mount() here. mount() → createEngine →
  // getCornerstone() throws "Cornerstone not initialized" during this synchronous
  // render on a cold full-reload (initializeCornerstone runs later, in
  // SessionVolumeMount's IIFE), which left the §9.9 context-loss listeners
  // unattached. The OBJECT is created synchronously (cheap, no Cornerstone), and
  // mount() (engine + listener attach) is deferred to the effect below.
  // Ref-lazy (not useState-once): the flag can flip false→true on a LATER
  // render when a classification study's series metadata resolves after the
  // grid first mounted (reslice becomes wanted). A useState lazy initializer
  // runs only on first render, so it would leave the workspace null forever and
  // every unified pane would fail with "no shared workspace". Creating it during
  // whichever render first sees the flag true keeps the synchronous-creation
  // guarantee (children see it in context on their first render) for both the
  // initial-mount (segmentation) and the later-flip (reslice) cases. Only the
  // cheap OBJECT is created here; mount() (engine + listeners) is deferred to
  // the effect below, guarded against double-mount.
  const workspaceRef = useRef<ViewportWorkspace | null>(null);
  if (newViewportFlag && !workspaceRef.current) {
    workspaceRef.current = new ViewportWorkspace(createProductionWorkspaceDeps());
  }
  const sharedWorkspace = workspaceRef.current;
  // Attach the engine + the single set of webglcontextlost/restored listeners
  // AFTER Cornerstone initializes. Guarded so React StrictMode's double-invoke
  // (and any re-run) can't mount twice — a second mount() would leak a second
  // engine + listener set without detaching the first. dispose() on unmount is
  // the ONLY place the engine is destroyed.
  const workspaceMountedRef = useRef(false);
  useEffect(() => {
    if (!sharedWorkspace || workspaceMountedRef.current) return;
    let cancelled = false;
    initializeCornerstone()
      .then(() => {
        if (cancelled || workspaceMountedRef.current) return;
        workspaceMountedRef.current = true;
        sharedWorkspace.mount();
      })
      .catch((err) => {
        logger.error("[HangingProtocolGrid] shared workspace mount failed:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [sharedWorkspace]);
  // dispose() on grid unmount is the ONLY place the shared engine is destroyed.
  useEffect(() => {
    return () => {
      try {
        sharedWorkspace?.dispose();
      } catch (err) {
        logger.warn("[HangingProtocolGrid] shared workspace dispose failed:", err);
      }
    };
  }, [sharedWorkspace]);

  // ─── A4 consolidation C2: the SINGLE layout-level segmentation owner ────────
  // Flag-gated. Exactly ONE writer sets `segmentationVolumeIdAtom` (the gate
  // that ungates SegmentationAnnotationPanel) — deterministically the priority
  // series' labelmap — and pre-initializes segment metadata on its paired
  // contour seg BEFORE the gate flips. This replaces the old per-pane
  // (last-writer-wins) gate writes that raced so the active contour seg / its
  // initialized segments could disagree with the gate: the root cause of the
  // saved-mask `'locked'` restore crash and the draw segmentIndex desync on the
  // new path. Flag OFF → inert; the old VolumeViewport owns the gate as before.
  const setSegmentationVolumeId = useSetAtom(segmentationVolumeIdAtom);
  const layoutPrioritySeriesId = useAtomValue(prioritySeriesIdAtom);
  const layoutSavedAnnotations = useAtomValue(savedAnnotationsAtom);
  const layoutActiveSegmentIndex = useAtomValue(activeSegmentIndexAtom);
  // Per-pane labelmap-ready signals, read per slot (set by each
  // SessionVolumeMount when its labelmap + segmentation register). Empty slots
  // fall back to a slot-UNIQUE key (not a shared "") so two empty slots can't
  // couple through one seriesSegReadyFamily("") atom.
  const segReadyTopLeft = useAtomValue(
    seriesSegReadyFamily(slotTopLeft.seriesId || "__empty:top-left"),
  );
  const segReadyTopRight = useAtomValue(
    seriesSegReadyFamily(slotTopRight.seriesId || "__empty:top-right"),
  );
  const segReadyBottomLeft = useAtomValue(
    seriesSegReadyFamily(slotBottomLeft.seriesId || "__empty:bottom-left"),
  );
  const segReadyBottomRight = useAtomValue(
    seriesSegReadyFamily(slotBottomRight.seriesId || "__empty:bottom-right"),
  );
  useEffect(() => {
    if (!newViewportFlag) return;

    // Map each slot's series → its labelmap-ready signal (labelmap id or null).
    const readyBySeries = new Map<string, string | null>();
    if (slotTopLeft.seriesId) readyBySeries.set(slotTopLeft.seriesId, segReadyTopLeft);
    if (slotTopRight.seriesId) readyBySeries.set(slotTopRight.seriesId, segReadyTopRight);
    if (slotBottomLeft.seriesId) readyBySeries.set(slotBottomLeft.seriesId, segReadyBottomLeft);
    if (slotBottomRight.seriesId) readyBySeries.set(slotBottomRight.seriesId, segReadyBottomRight);

    // Deterministic gate series: when a priority series is assigned to a slot,
    // wait for IT specifically (it loads first — no fallback-then-switch churn);
    // otherwise the first slot (in SLOT_IDS order) whose seg is ready.
    let gateLabelmap: string | null = null;
    if (layoutPrioritySeriesId) {
      gateLabelmap = readyBySeries.get(layoutPrioritySeriesId) ?? null;
    } else {
      const orderedSeries = [
        slotTopLeft.seriesId,
        slotTopRight.seriesId,
        slotBottomLeft.seriesId,
        slotBottomRight.seriesId,
      ];
      for (const sid of orderedSeries) {
        const ready = sid ? readyBySeries.get(sid) : null;
        if (ready) {
          gateLabelmap = ready;
          break;
        }
      }
    }
    if (!gateLabelmap) return; // chosen series' segmentation not ready yet

    try {
      const gateContourSegId = getContourSegmentationId(gateLabelmap);
      // Register + activate the gate's contour seg on every enabled viewport
      // (idempotent; the same call the old path makes per-pane). Late-mounting
      // panes re-assert this via their own effect once the gate is set.
      ensureContourSegmentationActive(gateLabelmap, layoutActiveSegmentIndex);
      // Pre-init segment metadata for 1..max(saved segment_index, active) on the
      // contour seg BEFORE the gate flips. `setActiveSegmentIndex` lazily creates
      // segments[i] = { locked:false, … }; niftiMaskLoader's restore only ensures
      // segment 1, so without this a saved mask whose segment_index exceeds the
      // initialized set throws "Cannot read properties of undefined (reading
      // 'locked')" in addContourSegmentationAnnotation and the contour never
      // renders. Re-runs idempotently when savedAnnotations lands after the gate.
      const maxSaved = layoutSavedAnnotations.reduce(
        (m, a) => Math.max(m, a.segment_index ?? 0),
        0,
      );
      const maxIdx = Math.max(maxSaved, layoutActiveSegmentIndex, 1);
      const segIndexApi = getCornerstoneTools().segmentation?.segmentIndex;
      for (let i = 1; i <= maxIdx; i++) {
        segIndexApi?.setActiveSegmentIndex?.(gateContourSegId, i);
      }
      // Leave the active segment index at the live drawing target.
      segIndexApi?.setActiveSegmentIndex?.(gateContourSegId, layoutActiveSegmentIndex);
    } catch (err) {
      logger.error("[HangingProtocolGrid] layout segmentation owner setup failed:", err);
    }

    // Set the gate. Jotai swallows same-value writes, so the repeated set on
    // re-run (savedAnnotations / activeSegmentIndex change) is a no-op and won't
    // re-trigger consumers.
    setSegmentationVolumeId(gateLabelmap);
  }, [
    newViewportFlag,
    layoutPrioritySeriesId,
    layoutSavedAnnotations,
    layoutActiveSegmentIndex,
    segReadyTopLeft,
    segReadyTopRight,
    segReadyBottomLeft,
    segReadyBottomRight,
    slotTopLeft.seriesId,
    slotTopRight.seriesId,
    slotBottomLeft.seriesId,
    slotBottomRight.seriesId,
    setSegmentationVolumeId,
  ]);

  // Priority series loading - single action handles setting + spotlight
  const setPrioritySeries = useSetAtom(setPrioritySeriesAtom);

  // Register sibling viewports callback for cross-plane contour rendering.
  // Slot→plane is static (top-left=axial, top-right=coronal, bottom-left=
  // sagittal). A companion is OMITTED when its slot is not mounted — in the
  // "seg-2up" layout only one companion is rendered at a time. Axial is the
  // anchor: without it there is no segmentation. Reporting only mounted planes
  // is what makes nnInteractive's first-click seeding (planesToCall = mounted
  // planes with a series) automatically cover just the visible planes.
  useEffect(() => {
    setGetSiblingViewportsCallback(() => {
      try {
        const cs = getCornerstone();
        const engine = cs.getRenderingEngine(SHARED_RENDERING_ENGINE_ID);
        if (!engine) return null;
        const allVps = engine.getViewports?.() ?? [];
        const axialVp = allVps.find((vp: any) => vp.id?.includes("top-left"));
        // Axial required (anchor plane). No axial viewport/series → no siblings.
        if (!axialVp || !slotTopLeft.seriesId) return null;
        const hasContrast = (seriesUid: string | null) =>
          !!(seriesUid && seriesMetadata?.[seriesUid]?.has_contrast);
        const result: SiblingViewports = {
          axial: {
            viewport: axialVp,
            seriesUid: slotTopLeft.seriesId,
            hasContrast: hasContrast(slotTopLeft.seriesId),
          },
        };
        const coronalVp = allVps.find((vp: any) => vp.id?.includes("top-right"));
        if (coronalVp && slotTopRight.seriesId) {
          result.coronal = {
            viewport: coronalVp,
            seriesUid: slotTopRight.seriesId,
            hasContrast: hasContrast(slotTopRight.seriesId),
          };
        }
        const sagittalVp = allVps.find((vp: any) => vp.id?.includes("bottom-left"));
        if (sagittalVp && slotBottomLeft.seriesId) {
          result.sagittal = {
            viewport: sagittalVp,
            seriesUid: slotBottomLeft.seriesId,
            hasContrast: hasContrast(slotBottomLeft.seriesId),
          };
        }
        return result;
      } catch (err) {
        logger.warn("[HangingProtocolGrid] getSiblingViewports lookup failed:", err);
        return null;
      }
    });
    return () => setGetSiblingViewportsCallback(null);
  }, [
    slotTopLeft.seriesId,
    slotTopRight.seriesId,
    slotBottomLeft.seriesId,
    studyMetadata.series_metadata,
  ]);

  // Bridge nnInteractive's segmentation-result callback to the jotai atom
  // consumed by SegmentationViewerPanel (bottom-right slot's 3D viewer +
  // stats). Without this, `_onNnSegmentationResult` stays null,
  // `updateMultiSeries3DViewer` early-returns, and the 3D panel never
  // populates.
  const setNnSegResult = useSetAtom(nnSegmentationResultAtom);
  const triggerCounterRef = useRef(0);
  useEffect(() => {
    // The atom requires a `trigger` field used elsewhere as a re-render
    // signal; the tool's result payload doesn't carry one. A monotonic
    // counter is stricter than Date.now() (no same-ms collision risk on
    // rapid back-to-back results).
    setNnSegmentationResultCallback((result) => {
      triggerCounterRef.current += 1;
      setNnSegResult(result ? { ...result, trigger: triggerCounterRef.current } : null);
    });
    return () => setNnSegmentationResultCallback(null);
  }, [setNnSegResult]);

  // Cross-viewport sync: after nnInteractive segmentation, snap all planes
  // to the center of the segmented region (#216: added axial sync).
  // Slot state is read via refs so the handler closure never goes stale.
  const slotTopLeftRef = useRef(slotTopLeft);
  const slotTopRightRef = useRef(slotTopRight);
  const slotBottomLeftRef = useRef(slotBottomLeft);
  slotTopLeftRef.current = slotTopLeft;
  slotTopRightRef.current = slotTopRight;
  slotBottomLeftRef.current = slotBottomLeft;
  // In seg-2up only one companion plane is mounted. Writing the hidden
  // (unmounted) companion's sliceIndex here would silently reposition it — after
  // a 2×2 visit populated its totalSlices, an axial seed would reset it to
  // mid-volume, so it opens at the wrong slice on switch-back. Gate the companion
  // writes on the slot being mounted. Ref so the bus closure never goes stale.
  const hiddenCompanionSlotRef = useRef<SlotId | null>(null);
  hiddenCompanionSlotRef.current = isSeg2Up ? hiddenCompanionSlotId : null;
  useEffect(() => {
    const handler = busOn("nnCrossViewportSync", ({ axial, coronal, sagittal }) => {
      const axialTotal = slotTopLeftRef.current.totalSlices;
      if (axialTotal > 1) {
        const axialSlice = Math.round(axial * (axialTotal - 1));
        setSlotTopLeft((prev) => ({
          ...prev,
          sliceIndex: Math.max(0, Math.min(axialSlice, axialTotal - 1)),
        }));
      }
      const coronalTotal = slotTopRightRef.current.totalSlices;
      if (coronalTotal > 1 && hiddenCompanionSlotRef.current !== "top-right") {
        const coronalSlice = Math.round(coronal * (coronalTotal - 1));
        setSlotTopRight((prev) => ({
          ...prev,
          sliceIndex: Math.max(0, Math.min(coronalSlice, coronalTotal - 1)),
        }));
      }
      const sagittalTotal = slotBottomLeftRef.current.totalSlices;
      if (sagittalTotal > 1 && hiddenCompanionSlotRef.current !== "bottom-left") {
        const sagittalSlice = Math.round(sagittal * (sagittalTotal - 1));
        setSlotBottomLeft((prev) => ({
          ...prev,
          sliceIndex: Math.max(0, Math.min(sagittalSlice, sagittalTotal - 1)),
        }));
      }
    });
    return () => busOff("nnCrossViewportSync", handler);
  }, [setSlotTopLeft, setSlotTopRight, setSlotBottomLeft]);

  const seriesMetadata = studyMetadata.series_metadata;
  const isSupported = hasHangingProtocolSupport(seriesMetadata);
  const jotaiStore = useStore();
  const handleSeriesLoadComplete = useCallback(
    (seriesId: string, totalFrames: number) => {
      // Mark the series volume as ready so dependent UI (e.g. the sidebar
      // thumbnail) can switch from a muted to a full-contrast preview.
      jotaiStore.set(volumeStatusFamily(seriesId), "ready");

      const tracker = defaultViewLoadRef.current;
      if (!tracker || tracker.studyId !== studyId) {
        return;
      }
      if (!tracker.expectedSeries.has(seriesId)) {
        return;
      }
      if (tracker.completedSeries.has(seriesId)) {
        return;
      }

      tracker.completedSeries.add(seriesId);
      tracker.completedSlices += totalFrames;

      if (tracker.completedSeries.size === tracker.expectedSeries.size) {
        perfMeasure("default-view-all-slices", tracker.markName);
      }
    },
    [studyId, jotaiStore],
  );

  // Helper to get slot state by id
  const getSlotState = (slotId: SlotId) => {
    switch (slotId) {
      case "top-left":
        return slotTopLeft;
      case "top-right":
        return slotTopRight;
      case "bottom-left":
        return slotBottomLeft;
      case "bottom-right":
        return slotBottomRight;
    }
  };

  // Helper to set slot state by id
  const setSlotState = useCallback(
    (slotId: SlotId, state: ReturnType<typeof createDefaultSlotState>) => {
      switch (slotId) {
        case "top-left":
          setSlotTopLeft(state);
          break;
        case "top-right":
          setSlotTopRight(state);
          break;
        case "bottom-left":
          setSlotBottomLeft(state);
          break;
        case "bottom-right":
          setSlotBottomRight(state);
          break;
      }
    },
    [setSlotTopLeft, setSlotTopRight, setSlotBottomLeft, setSlotBottomRight],
  );

  // Reset all slots to default state
  const resetAllSlots = useCallback(() => {
    SLOT_IDS.forEach((slotId) => {
      setSlotState(slotId, createDefaultSlotState());
    });
    setActiveSlot(DEFAULT_SLOT);
    setMaximizedSlot(null);
  }, [setSlotState, setActiveSlot, setMaximizedSlot]);

  // Initialize slots with series assignments
  const initializeSlots = useCallback(
    (
      assignments: Record<string, string | null>,
      seriesMeta: Record<string, { window_center?: number | null; window_width?: number | null }>,
      instanceCounts: Record<string, number>,
    ) => {
      SLOT_IDS.forEach((slotId) => {
        const seriesId = assignments[slotId] ?? null;
        const totalSlices = seriesId ? (instanceCounts[seriesId] ?? 0) : 0;

        // Always open at the standard (soft-tissue) window, ignoring the
        // DICOM-embedded W/L (seriesMeta.window_center/width), so every study
        // opens to a predictable, consistent contrast rather than whatever the
        // scanner/PACS baked in. The user can still switch presets (T/L/B/R/V)
        // or drag W/L after load; Reset (R) returns here.
        const windowing = { ...DEFAULT_WINDOWING };

        // Coronal/sagittal start at middle slice
        const slotConfig = getSlotConfigs(isSegmentationProject)[slotId];
        const isCoronalOrSagittal =
          slotConfig.plane === "Coronal" || slotConfig.plane === "Sagittal";
        const sliceIndex = isCoronalOrSagittal && totalSlices > 0 ? Math.floor(totalSlices / 2) : 0;

        const seriesInstances = seriesId ? studyMetadata.series?.[seriesId] : undefined;
        const isInverted = shouldAutoInvert(seriesInstances);
        if (seriesId) warnOnMixedSignedTransferSyntax(seriesId, seriesInstances);

        setSlotState(slotId, {
          ...createDefaultSlotState(),
          seriesId,
          totalSlices,
          sliceIndex,
          windowing,
          initialWindowing: windowing,
          isInverted,
        });
      });

      setActiveSlot(DEFAULT_SLOT);
      setMaximizedSlot(null);

      const expectedSeries = new Set<string>();
      let totalSlices = 0;
      SLOT_IDS.forEach((slotId) => {
        const seriesId = assignments[slotId] ?? null;
        if (seriesId) {
          const count = instanceCounts[seriesId] ?? 0;
          if (count > 0) {
            expectedSeries.add(seriesId);
            totalSlices += count;
          }
        }
      });

      if (expectedSeries.size > 0) {
        if (defaultViewLoadRef.current?.studyId === studyId) {
          return;
        }
        const markName = `default-view-all-slices-start-${studyId}-${Date.now()}`;
        perfMark(markName);
        defaultViewLoadRef.current = {
          studyId,
          markName,
          expectedSeries,
          completedSeries: new Set(),
          totalSlices,
          completedSlices: 0,
        };
        logger.debug(
          `[PERF] Default view all-series load started: ` +
            `${expectedSeries.size} series, ${totalSlices} total slices`,
        );
      } else {
        defaultViewLoadRef.current = null;
      }
    },
    [setSlotState, setActiveSlot, setMaximizedSlot, studyMetadata.series],
  );

  // Full acquisition switch: re-assign ALL viewports using only series from the
  // new acquisition group (FrameOfReferenceUID + AcquisitionNumber), prioritizing
  // the dropped series into the slot the user dropped on.
  const applyAcquisitionSwitch = useCallback(
    (droppedSeriesId: string, targetSlotId: SlotId, newGroup: string) => {
      if (!seriesMetadata || !studyMetadata.series) return;

      // Filter series metadata to only those sharing the new acquisition group
      const filteredMetadata = Object.fromEntries(
        Object.entries(seriesMetadata).filter(([, meta]) => getAcquisitionGroup(meta) === newGroup),
      );

      // Re-run hanging protocol with the filtered set, prioritizing the dropped series
      const assignments = assignSeriesToSlots(filteredMetadata, {
        prioritySeriesId: droppedSeriesId,
        isSegmentation: isSegmentationProject,
        filterNonKidneyAnatomy,
      });

      // assignSeriesToSlots forces the priority series into top-left, but the user
      // may have dropped onto a different slot. Swap to honor the user's intent.
      if (targetSlotId !== "top-left" && assignments["top-left"] === droppedSeriesId) {
        const displaced = assignments[targetSlotId];
        assignments[targetSlotId] = droppedSeriesId;
        assignments["top-left"] = displaced;
      }

      const instanceCounts = Object.fromEntries(
        Object.entries(studyMetadata.series ?? {}).map(([id, instances]) => [id, instances.length]),
      );

      if (import.meta.env.DEV) {
        logger.debug("[HangingProtocol] Switching acquisition group:", newGroup);
        logger.debug("[HangingProtocol] New assignments:", assignments);
      }
      initializeSlots(assignments, seriesMetadata, instanceCounts);
    },
    [
      seriesMetadata,
      studyMetadata.series,
      isSegmentationProject,
      filterNonKidneyAnatomy,
      initializeSlots,
    ],
  );

  // Acquisition group check on a series drop. On a mismatch we no longer block
  // with a modal (which froze the whole viewer) — we warn softly via a toast
  // with a one-click revert. Behavior splits by project type:
  //   - Segmentation projects: a mixed-acquisition layout breaks cross-plane
  //     sync (reference lines, segmentation), so we apply the FULL acquisition
  //     switch immediately and offer Revert.
  //   - Everything else (non-seg labeling, radiologist review): place just the
  //     dropped series (mixing is allowed) and offer Undo.
  // Both restore from a snapshot of the pre-drop slot states.
  const handleBeforeSeriesDrop = useCallback(
    (droppedSeriesId: string, targetSlotId: SlotId): boolean => {
      if (!seriesMetadata) return true;

      // Build current assignments from slot states
      const currentAssignments: Record<SlotId, string | null> = {
        "top-left": slotTopLeft.seriesId,
        "top-right": slotTopRight.seriesId,
        "bottom-left": slotBottomLeft.seriesId,
        "bottom-right": slotBottomRight.seriesId,
      };

      const dominantGroup = getDominantAcquisitionGroup(currentAssignments, seriesMetadata);
      const mismatch = checkAcquisitionGroupMismatch(
        droppedSeriesId,
        dominantGroup,
        seriesMetadata,
      );

      if (!mismatch) return true; // Same acquisition or unknown — allow

      // Snapshot the pre-drop slot states so the toast can revert in one click.
      // Jotai state is immutable, so these references survive the drop's writes.
      const priorStates = SLOT_IDS.map((sid) => [sid, getSlotState(sid)] as const);
      const restore = () => {
        priorStates.forEach(([sid, state]) => setSlotState(sid, state));
      };

      if (isSegmentationProject) {
        // No mixed acquisitions in segmentation projects: switch everything to
        // the new group now, and offer Revert.
        applyAcquisitionSwitch(droppedSeriesId, targetSlotId, mismatch.newGroup);
        busEmit("toast", {
          message: "Switched to a different acquisition to keep views aligned.",
          action: { label: "Revert", onPress: restore },
          durationMs: ACQUISITION_TOAST_DURATION_MS,
        });
        return false; // Superseded by the full switch — skip the single-slot drop
      }

      // Non-segmentation: allow the single-slot drop (a mix is acceptable here),
      // but warn that cross-plane features may not line up, with an Undo.
      busEmit("toast", {
        message: "Different acquisition — reference lines & segmentation sync may not align.",
        action: { label: "Undo", onPress: restore },
        durationMs: ACQUISITION_TOAST_DURATION_MS,
      });
      return true; // Let the single-slot drop proceed
    },
    [
      seriesMetadata,
      slotTopLeft.seriesId,
      slotTopRight.seriesId,
      slotBottomLeft.seriesId,
      slotBottomRight.seriesId,
      isSegmentationProject,
      applyAcquisitionSwitch,
      getSlotState,
      setSlotState,
    ],
  );

  // Swap-aware series drop: when a series is dropped onto a slot, if that series
  // is already displayed in another slot, swap the two slots instead of replacing.
  // This ensures all 3 planes stay visible at all times.
  const handleSeriesDrop = useCallback(
    (droppedSeriesId: string, targetSlotId: SlotId) => {
      const targetState = getSlotState(targetSlotId);
      const displacedSeriesId = targetState.seriesId;

      // Find which slot (if any) currently holds the dropped series
      let sourceSlotId: SlotId | null = null;
      for (const sid of SLOT_IDS) {
        if (sid === targetSlotId) continue;
        if (getSlotState(sid).seriesId === droppedSeriesId) {
          sourceSlotId = sid;
          break;
        }
      }

      // Always land on the standard (soft-tissue) window, ignoring DICOM W/L —
      // same rule as initial load, so a manual reassignment opens at the same
      // predictable contrast. Reset (R) returns here too.
      const getWindowing = (_sid: string | null) => ({ ...DEFAULT_WINDOWING });

      // Assign the dropped series to the target slot
      const droppedInstances = studyMetadata.series?.[droppedSeriesId];
      warnOnMixedSignedTransferSyntax(droppedSeriesId, droppedInstances);
      const droppedWindowing = getWindowing(droppedSeriesId);
      setSlotState(targetSlotId, {
        ...createDefaultSlotState(),
        seriesId: droppedSeriesId,
        windowing: droppedWindowing,
        initialWindowing: droppedWindowing,
        isInverted: shouldAutoInvert(droppedInstances),
      });

      // If the dropped series was in another slot, move the displaced series there (swap)
      if (sourceSlotId && displacedSeriesId) {
        const displacedInstances = studyMetadata.series?.[displacedSeriesId];
        warnOnMixedSignedTransferSyntax(displacedSeriesId, displacedInstances);
        const displacedWindowing = getWindowing(displacedSeriesId);
        setSlotState(sourceSlotId, {
          ...createDefaultSlotState(),
          seriesId: displacedSeriesId,
          windowing: displacedWindowing,
          initialWindowing: displacedWindowing,
          isInverted: shouldAutoInvert(displacedInstances),
        });
        if (import.meta.env.DEV) {
          logger.debug(
            `[HangingProtocol] Swapped: ${targetSlotId} (${droppedSeriesId.slice(-8)}) <-> ${sourceSlotId} (${displacedSeriesId.slice(-8)})`,
          );
        }
      } else if (import.meta.env.DEV) {
        logger.debug(`[HangingProtocol] Assigned ${droppedSeriesId.slice(-8)} to ${targetSlotId}`);
      }
    },
    [getSlotState, setSlotState, studyMetadata.series],
  );

  // A sidebar reformat request. The grid owns this rather than the sidebar so a
  // reformat passes the same acquisition-group guard as a drag-drop assignment
  // (handleBeforeSeriesDrop: whole-grid switch in segmentation, warn+Undo toast
  // otherwise). Deliberately NOT handleSeriesDrop: that swaps when the series is
  // already on screen, and the source axial must stay in its own pane while a
  // reformat of it appears in another.
  const handleReformatRequest = useCallback(
    (requestedSeriesId: string, plane: PlaneOrientation) => {
      // Segmentation layouts mount native cor/sag panes and resolveGridReslice
      // refuses to reslice there. Setting `reslice` anyway would leave the
      // slot holding the AXIAL series under a Coronal label with no reslice and
      // no badge — a silent wrong-plane display. Refuse loudly instead. Pulled
      // into a pure function (resolveReformatTarget) so this rule is unit-tested
      // without mounting the grid.
      const target = resolveReformatTarget(plane, isSegmentationProject);
      if ("refused" in target) {
        logger.warn("[HangingProtocol] reformat request refused", {
          reason: target.refused,
          seriesId: requestedSeriesId,
          plane,
        });
        return;
      }
      const targetSlotId = target.slotId;
      // Same guard every slot write goes through. `false` means the guard already
      // superseded this request (segmentation acquisition switch) — stop here.
      // Unreachable today (resolveReformatTarget already refused the segmentation
      // case above), kept to honor handleBeforeSeriesDrop's contract in case that
      // rule ever moves; the acquisition-mismatch toast is still the reason this
      // call exists.
      if (!handleBeforeSeriesDrop(requestedSeriesId, targetSlotId)) return;

      // Force-normalize the layout BEFORE writing the slot, so the destination
      // pane is guaranteed visible. `resolveReformatTarget` picks a FIXED slot
      // per plane, but a fixed slot isn't necessarily an ON-SCREEN slot:
      // `getSlotStyle` hides bottom-left/bottom-right in the "2x1" layout and
      // every non-focused slot in focus mode, and the slot loop below skips the
      // hidden companion in "seg-2up". Without this, e.g. a "2×1 Axial" layout
      // + a Coronal reformat writes to bottom-left, which 2x1 hides — nothing
      // appears — AND `setActiveSlot` below then points at that invisible pane,
      // so `scrollSliceAtom`/`resetSlotViewAtom`/`toggleInvertAtom` (all keyed
      // off `activeSlotAtom`) silently act on a pane the reader cannot see:
      // ↑/↓, R, and I feel dead until they click a visible viewport.
      //
      // Deliberately NOT a "is this slot visible" predicate — that would
      // duplicate getSlotStyle's per-layout rules and drift from them the next
      // time a layout changes. A reformat request means "show me this plane",
      // and 2x2 is the only layout where every plane is always visible
      // (effectiveLayoutMode collapses seg-2up to 2x2 once layoutMode is 2x2
      // too), so normalizing the layout is both simpler and always correct.
      // This deliberately overrides whatever layout the user had chosen — the
      // alternative is a silent no-op reformat, which is worse.
      setFocusMode({ enabled: false, slotId: null });
      setLayoutMode("2x2");

      const instances = studyMetadata.series?.[requestedSeriesId];
      warnOnMixedSignedTransferSyntax(requestedSeriesId, instances);
      setSlotState(
        targetSlotId,
        buildReformatSlotState(requestedSeriesId, plane, shouldAutoInvert(instances)),
      );
      setActiveSlot(targetSlotId);
      logger.info("[HangingProtocol] reformat assigned", {
        seriesId: requestedSeriesId,
        plane,
        targetSlotId,
      });
    },
    [
      isSegmentationProject,
      handleBeforeSeriesDrop,
      studyMetadata.series,
      setSlotState,
      setActiveSlot,
      setFocusMode,
      setLayoutMode,
    ],
  );

  // Sidebar → grid: subscribe to reformat requests. `handleReformatRequest` is
  // held in a ref and subscribed ONCE ([] deps) rather than re-subscribed on
  // every identity change: its dependency chain runs through `getSlotState`
  // (~line 631), an unmemoized arrow closing over the four full SlotState
  // objects, which change on nearly every render (scroll/W-L/zoom ticks all
  // write a new slot object). Memoizing `getSlotState` with `useCallback`
  // would not help — its real deps churn just as often — so it would still
  // tear down and re-register this subscription on nearly every render. The
  // ref sidesteps that: the effect subscribes once, and the handler always
  // reads the latest closure via the ref.
  const handleReformatRequestRef = useRef(handleReformatRequest);
  handleReformatRequestRef.current = handleReformatRequest;
  useEffect(() => {
    const handler = busOn("seriesReformatRequest", ({ seriesId: requested, plane }) => {
      handleReformatRequestRef.current(requested, plane);
    });
    return () => busOff("seriesReformatRequest", handler);
  }, []);

  // Reset state synchronously on mount - ensures fresh state before first paint.
  // Using useLayoutEffect prevents the flash of stale series from previous study.
  // Note: Empty deps ensures this ONLY runs on mount, not when callbacks change reference.
  useLayoutEffect(() => {
    resetAllSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Engine destruction is owned by the last-viewport-unmount path inside
  // VolumeViewport (see disableSharedViewport). Per-Grid unmount destroy is
  // wrong for the shared-engine pattern because the standalone fallback
  // viewport (CaseViewer single-viewport path) still needs the engine when
  // Grid is hidden.

  // Volume-cache LRU: keep the N most-recently-bound volumes resident so
  // chip-tabbing between two series doesn't re-allocate the ~500 MB packed
  // Float32 buffer on every swap. Beyond N, dispose LRU non-bound volumes —
  // disposeAndRemoveVolume calls vtkOpenGLTexture.releaseGraphicsResources()
  // + .delete() that cornerstone's _decacheVolume forgets, so the GPU
  // texture is actually released instead of leaking until WebGL context
  // exhaustion (the "white-out" symptom on long sessions).
  const volumeLruRef = useRef<string[]>([]);
  useEffect(() => {
    // FULL volume ids (`cornerstoneStreamingImageVolume:<study>_<series>`) for
    // the bound slots. The LRU stores full ids so eviction across a case switch
    // disposes the PRIOR study's volume (with its own studyId prefix) instead of
    // a `<currentStudy>_<priorSeries>` id that was never cached — the leak that
    // exhausted WebGL contexts and blanked panes until refresh.
    //
    // A reslice (reformat) slot's own `seriesId` is null — its render binds the
    // SOURCE series' volume instead (see the slot-render `seriesId` fallback
    // below) — so `reslice?.sourceSeriesId` must be included here too, or a
    // volume held resident ONLY by a reformat pane would look unbound and get
    // evicted out from under the pane still displaying it.
    const boundVolumeIds = [
      slotTopLeft.seriesId,
      slotTopLeft.reslice?.sourceSeriesId,
      slotTopRight.seriesId,
      slotTopRight.reslice?.sourceSeriesId,
      slotBottomLeft.seriesId,
      slotBottomLeft.reslice?.sourceSeriesId,
      slotBottomRight.seriesId,
      slotBottomRight.reslice?.sourceSeriesId,
    ]
      .filter((id): id is string => !!id)
      .map((seriesId) => volumeIdFor(studyId, seriesId));

    const { nextLru, evicted } = computeVolumeLru({
      boundVolumeIds,
      priorLru: volumeLruRef.current,
      cap: VOLUME_LRU_CAP,
    });

    for (const volumeId of evicted) {
      const segmentationId = `${volumeBaseFromId(volumeId)}_segmentation`;
      const disposed = disposeAndRemoveVolume(volumeId);
      // Segmentation volumes never carry a vtkOpenGLTexture we own, so the
      // plain removeVolumeLoadObject path is fine for them.
      try {
        getCornerstone().cache.removeVolumeLoadObject(segmentationId);
      } catch (e) {
        logger.warn("[HangingProtocolGrid] segmentation_evict_failed", {
          segmentationId,
          err: String(e),
        });
      }
      logger.warn("[HangingProtocolGrid] volume_lru_evicted", {
        volumeId,
        disposed,
        residentCount: nextLru.length,
      });
    }

    // C1: the count-LRU above only bounds the ≤cap volumes the grid binds to its
    // slots. But the grid stays mounted across case switches, so volumes from
    // every previously-visited study otherwise accumulate in the cache untracked
    // (observed 47 volumes / 14 studies pegging maxCacheSize → CACHE_SIZE_EXCEEDED
    // + viewer_webgl_context_lost). Purge every cached streaming-volume from a
    // study other than the current one, plus its segmentation companion.
    // CAVEAT: getCornerstone().cache is the app-wide cornerstone singleton, so
    // this disposes foreign-study volumes globally. Safe ONLY because this grid
    // is single-study and the leaked volumes are its own case history. If the
    // grid is ever co-mounted with a component that legitimately keeps another
    // study's volume resident (prior-study comparison, thumbnails), scope this
    // to the grid's own volume ids instead.
    try {
      const cache = getCornerstone().cache;
      const residentIds = (cache.getVolumes?.() ?? []).map(
        (v: { volumeId?: string; id?: string }) => v?.volumeId ?? v?.id ?? "",
      );
      const foreignIds = foreignStudyVolumeIds(residentIds, studyId);
      for (const volumeId of foreignIds) {
        disposeAndRemoveVolume(volumeId);
        try {
          cache.removeVolumeLoadObject(`${volumeBaseFromId(volumeId)}_segmentation`);
        } catch (e) {
          logger.warn("[HangingProtocolGrid] foreign_study_seg_evict_failed", {
            volumeId,
            err: String(e),
          });
        }
      }
      if (foreignIds.length) {
        logger.warn("[HangingProtocolGrid] foreign_study_volumes_purged", {
          count: foreignIds.length,
        });
      }
    } catch (e) {
      logger.warn("[HangingProtocolGrid] foreign_study_purge_failed", { err: String(e) });
    }

    volumeLruRef.current = nextLru;
  }, [
    slotTopLeft.seriesId,
    slotTopLeft.reslice,
    slotTopRight.seriesId,
    slotTopRight.reslice,
    slotBottomLeft.seriesId,
    slotBottomLeft.reslice,
    slotBottomRight.seriesId,
    slotBottomRight.reslice,
    studyId,
  ]);

  // Bound the IMAGE cache to the current study on each case switch. The volume
  // LRU + foreignStudyVolumeIds purge above only cover
  // `cornerstoneStreamingImageVolume:` VOLUMES; the irregular cor/sag reformats
  // render as StackViewports whose frames are plain `wadouri:` images with no
  // volume id, so nothing evicted them and they accumulated across every case a
  // reader opened. Once the image cache filled (CACHE_SIZE_EXCEEDED), a freshly
  // opened recon's end frames (fetched last / on scroll) could no longer be
  // cached and stayed blank while the middle survived — the "middle loads, ends
  // stay blank; refresh fixes it" report.
  //
  // Run this DEFERRED (post-commit macrotask), once per studyId, NOT inside the
  // volume-LRU effect: evicting synchronously during the effect flush — while the
  // outgoing case's volume is still cancelling its in-flight loads — raced React
  // into a "Maximum update depth" burst. A macrotask lets the case switch settle
  // first; cache cleanup is a side effect that never needs to block the commit.
  useEffect(() => {
    if (!studyId) return;
    const id = setTimeout(() => {
      try {
        const evicted = purgeForeignStudyImages(studyId);
        if (evicted) {
          logger.warn("[HangingProtocolGrid] foreign_study_images_purged", { count: evicted });
        }
      } catch (e) {
        logger.warn("[HangingProtocolGrid] foreign_study_images_purge_failed", { err: String(e) });
      }
    }, 0);
    return () => clearTimeout(id);
  }, [studyId]);

  // Coordinated resize across every viewport in the shared engine. Per-
  // VolumeViewport ResizeObservers used to race here: when a layout swap
  // (spotlight on/off, 2x2 ↔ 2x1, split drag) resized multiple slots in the
  // same frame, the first observer to fire called engine.resize, which runs
  // resetCameraForResize on every viewport — by the time observers 2-4 ran,
  // they captured the post-reset state and "restored" the reset.
  //
  // Worse, the per-viewport call used engine.resize(true, true), which
  // restores the OLD camera (parallelScale + focalPoint) on the NEW canvas,
  // so the projection no longer fit the new aspect ratio → image stretched
  // (issue #356, most visible in the 1+3 spotlight focus pane). The canonical
  // Cornerstone3D pattern uses keepCamera=false plus get/setViewPresentation
  // — that's what `resizeAndRestoreViewports` does. Slice isn't part of
  // ViewPresentation, so we override it from the slot atoms (authoritative
  // source) for `hangingProtocol_*` viewports.
  //
  // We capture *every* viewport in the engine (not just hp-prefixed ones)
  // because engine.resize is engine-wide; filtering by prefix would silently
  // drop the camera of any other viewport that shares the engine.
  useEffect(() => {
    if (!gridEl) return;

    const slotElements = Array.from(gridEl.querySelectorAll<HTMLDivElement>("[data-slot-id]"));
    if (slotElements.length === 0) return;

    let rafId = 0;
    const runCoordinatedResize = () => {
      let cs: ReturnType<typeof getCornerstone>;
      try {
        cs = getCornerstone();
      } catch (err) {
        // getCornerstone throws until the viewer module finishes init. Warn
        // (not debug — a prod no-op) so an init-order failure that silently
        // disables the coordinated resize surfaces in ops.
        logger.warn("hanging_protocol_grid resize_skip_cornerstone_unavailable:", err);
        return;
      }
      const engine = cs.getRenderingEngine(SHARED_RENDERING_ENGINE_ID);
      if (!engine) return;

      const allVps = (engine.getViewports?.() ?? []) as ResizableViewport[];

      // Restore presentation only for on-screen viewports (see
      // selectOnScreenViewports for why hidden 0×0 slots are excluded). The
      // earlier guard bailed the WHOLE coordinated resize when *any* slot was
      // 0×0; because 2x1 and single ALWAYS hide a slot, that permanently
      // disabled the re-fit in those modes — switching into 2x1 grew the top
      // slots to full height but never re-fit the projection, so the visible
      // viewport kept the previous layout's aspect ratio (the stretch this
      // fixes).
      const vps = selectOnScreenViewports(allVps);
      if (vps.length === 0) {
        // Every viewport collapsed — the grid panel itself is hidden, not a
        // layout swap. Warn so a layout that traps the grid here surfaces in
        // ops instead of looking like "resize never happened".
        logger.warn("hanging_protocol_grid resize_skip_all_collapsed count=%s", allVps.length);
        return;
      }

      const sliceIndexOverrides = new Map<string, number>();
      for (const vp of vps) {
        if (typeof vp.id !== "string" || !vp.id.startsWith("hangingProtocol_")) continue;
        const slotId = vp.id.slice("hangingProtocol_".length) as SlotId;
        if (!(SLOT_IDS as readonly string[]).includes(slotId)) continue;
        sliceIndexOverrides.set(vp.id, jotaiStore.get(slotStateFamily(slotId)).sliceIndex);
      }

      resizeAndRestoreViewports(engine, vps, {
        jumpToSlice: cs.utilities.jumpToSlice,
        sliceIndexOverrides,
        logContext: "HangingProtocolGrid",
      });
    };

    const observer = new ResizeObserver(() => {
      // Coalesce multiple slot-cell resizes from the same layout swap into a
      // single coordinated resize per animation frame.
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(runCoordinatedResize);
    });
    slotElements.forEach((el) => observer.observe(el));

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [gridEl, jotaiStore]);

  // Initialize hanging protocol when data is available
  // Cache series reference to avoid accessing potentially stale studyMetadata during cleanup
  const seriesData = studyMetadata?.series;
  useEffect(() => {
    // Defensive: ensure all required data is present
    // Wait for prioritySeriesId to resolve (undefined = still loading, null = no priority)
    if (!isSupported || !seriesMetadata || !seriesData || prioritySeriesId === undefined) {
      return;
    }

    // Filter series_metadata to only include series that have instances
    // This prevents assigning series that exist in metadata but have no actual frames
    const availableSeriesIds = new Set(Object.keys(seriesData));
    const filteredSeriesMetadata = Object.fromEntries(
      Object.entries(seriesMetadata).filter(([seriesId]) => availableSeriesIds.has(seriesId)),
    );

    const assignments = assignSeriesToSlots(filteredSeriesMetadata, {
      prioritySeriesId: prioritySeriesId ?? undefined,
      isSegmentation: isSegmentationProject,
      filterNonKidneyAnatomy,
    });
    const instanceCounts = Object.fromEntries(
      Object.entries(seriesData).map(([id, instances]) => [id, instances.length]),
    );
    logger.debug("[HangingProtocol] Initializing slots with assignments:", assignments);
    logger.debug("[HangingProtocol] Instance counts:", instanceCounts);
    logger.debug(
      "[HangingProtocol] Series metadata:",
      Object.entries(filteredSeriesMetadata).map(([id, meta]) => ({
        id: id.substring(0, 20) + "...",
        desc: meta.series_description,
        plane: meta.plane,
        num: meta.num_instances,
      })),
    );
    initializeSlots(assignments, seriesMetadata, instanceCounts);
  }, [
    studyId,
    seriesMetadata,
    isSupported,
    seriesData,
    initializeSlots,
    prioritySeriesId,
    isSegmentationProject,
    filterNonKidneyAnatomy,
  ]);

  // Set priority series after slots are initialized (or when prop changes)
  // The priority series must be one that's actually assigned to a slot (otherwise gating is a no-op).
  // Prefer: selectedSeriesId (if in a slot) > top-left axial > top-right axial
  useEffect(() => {
    const allSlotSeries = [
      slotTopLeft.seriesId,
      slotTopRight.seriesId,
      slotBottomLeft.seriesId,
      slotBottomRight.seriesId,
    ];
    const isInSlot = prioritySeriesId && allSlotSeries.includes(prioritySeriesId);

    if (isInSlot) {
      // Priority series is in a slot — set it without spotlight so all views load equally
      setPrioritySeries({ seriesId: prioritySeriesId, spotlight: false });
    } else {
      // Selected series not in any slot (e.g. scout) — prioritize the axial slot
      const axialPriority = slotTopLeft.seriesId || slotTopRight.seriesId;
      setPrioritySeries({ seriesId: axialPriority ?? null, spotlight: false });
    }
  }, [
    prioritySeriesId,
    setPrioritySeries,
    slotTopLeft.seriesId,
    slotTopRight.seriesId,
    slotBottomLeft.seriesId,
    slotBottomRight.seriesId,
  ]);

  // Handle keyboard shortcuts and navigation
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Skip shortcuts when typing in input fields (fixes letter 'l' bug in report editor)
      const target = event.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) {
        return;
      }

      // Up/Down arrows for slice scrolling — handled before the repeat/modifier
      // guard below so that holding the key auto-repeats through slices. The
      // letter shortcuts (w/h/p/z/l/r/i/x) keep the no-repeat guard so a long
      // press doesn't spam tool switches.
      if (
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey &&
        !event.shiftKey &&
        (event.key === "ArrowUp" || event.key === "ArrowDown")
      ) {
        event.preventDefault();
        scrollSlice(event.key === "ArrowUp" ? "up" : "down");
        return;
      }

      if (event.repeat || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
      const key = event.key.toLowerCase();

      // Tool shortcuts: W/H=HU (window-level), P=Pan, Z=Zoom, L=Length,
      // M=Rectangle ROI. Pressing the same tool key again restores the previous
      // drawing tool. (Bare-letter tool keys live here, NOT in useViewerHotkeys —
      // binding them in both fires setActiveTool twice and the toggle cancels.)
      if (key === "w" || key === "h") {
        event.preventDefault();
        runCommand("setActiveTool", { tool: "none" });
        return;
      }
      if (key === "p") {
        event.preventDefault();
        runCommand("setActiveTool", { tool: "pan" });
        return;
      }
      if (key === "z") {
        event.preventDefault();
        runCommand("setActiveTool", { tool: "zoom" });
        return;
      }
      if (key === "l") {
        event.preventDefault();
        runCommand("setActiveTool", { tool: "length" });
        return;
      }
      if (key === "m") {
        event.preventDefault();
        runCommand("setActiveTool", { tool: "rectangleROI" });
        return;
      }

      // R = reset view, I = invert, X = reference lines
      if (key === "r") {
        event.preventDefault();
        resetSlotView(activeSlot);
        return;
      }
      if (key === "i") {
        event.preventDefault();
        toggleInvert(activeSlot);
        return;
      }
      if (key === "x") {
        event.preventDefault();
        setReferenceLinesEnabled(!referenceLinesEnabled);
        return;
      }

      // Cycling is meaningless when one pane is maximized.
      if (maximizedSlot) return;

      // Tab is reserved for editor/suggestion-strip navigation (#404) and the
      // arrow keys navigate cases (prev/next), so "V" cycles the active viewport
      // pane through the populated slots (wrapping). Clicking a pane also activates it.
      if (key === "v") {
        event.preventDefault();
        // Cycle only panes that actually render a scrollable image viewport (a
        // cornerstone canvas). Non-scrollable panels (3D seg, phase summary) and
        // empty/stale slots have no such canvas and are skipped. Reading the
        // rendered DOM is layout-agnostic — unlike replicating per-slot render
        // conditions, which silently misses cases (e.g. a hidden slot's stale id).
        // NOTE: "cornerstone-canvas" is cornerstone3D's CANVAS_CSS_CLASS (from
        // getOrCreateCanvas); re-verify it on a cornerstone upgrade — there's no
        // failing test if the class name shifts.
        const scrollableSlots = ["top-left", "top-right", "bottom-left", "bottom-right"].filter(
          (slot) =>
            !!document
              .querySelector(`[data-series-drop-slot="${slot}"]`)
              ?.querySelector("canvas.cornerstone-canvas"),
        );
        const next = nextVisibleSlot(scrollableSlots, activeSlot);
        if (next) setActiveSlot(next);
      }
    },
    [
      activeSlot,
      maximizedSlot,
      setActiveSlot,
      resetSlotView,
      toggleInvert,
      scrollSlice,
      referenceLinesEnabled,
      setReferenceLinesEnabled,
    ],
  );

  // Register keyboard event listener
  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  // Fallback if no hanging protocol support
  if (!isSupported) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-bg-2 text-fg-muted">
        <div className="text-center">
          <p className="text-lg font-semibold mb-2">Hanging Protocol Not Available</p>
          <p className="text-sm">Series metadata not available for this study.</p>
        </div>
      </div>
    );
  }

  // Always render all 4 slots in the SAME order to preserve React component tree
  // Use CSS Grid positioning to achieve spotlight/focus layout without DOM structure changes
  const spotlightSlot = maximizedSlot;
  const isSpotlight = spotlightSlot !== null;
  const isFocusMode = focusMode.enabled && focusMode.slotId !== null;

  // Slots visible in each layout mode
  const TOP_ROW_SLOTS: SlotId[] = ["top-left", "top-right"];

  // Calculate grid position for each slot based on mode (focus > spotlight > layout > normal)
  const getSlotStyle = (slotId: SlotId): React.CSSProperties => {
    // Focus mode: single viewport fills entire area, others hidden
    if (isFocusMode) {
      if (slotId === focusMode.slotId) {
        return {
          gridColumn: "1",
          gridRow: "1",
        };
      }
      // Hidden but still in DOM - preserves Cornerstone.js viewport state
      return {
        display: "none",
      };
    }

    // Spotlight mode takes precedence over layout mode
    if (isSpotlight) {
      if (slotId === spotlightSlot) {
        // Spotlight slot: spans full height on left (75% width)
        return {
          gridColumn: "1",
          gridRow: "1 / 4",
        };
      }

      // Other slots: stacked vertically on right (25% width)
      const otherSlots = SLOT_IDS.filter((id) => id !== spotlightSlot);
      const rowIndex = otherSlots.indexOf(slotId);
      return {
        gridColumn: "2",
        gridRow: `${rowIndex + 1}`,
      };
    }

    // Layout mode handling
    if (layoutMode === "single") {
      // Single mode: only active slot visible
      if (slotId === activeSlot) {
        return {
          gridColumn: "1",
          gridRow: "1",
        };
      }
      return { display: "none" };
    }

    if (layoutMode === "2x1") {
      // 2x1 mode: only top row visible (axial views)
      if (TOP_ROW_SLOTS.includes(slotId)) {
        return {};
      }
      return { display: "none" };
    }

    if (isSeg2Up) {
      // seg-2up: large axial spans the full-height left column; the mounted
      // companion sits top-right; the 3D model sits bottom-right. The
      // non-companion plane's slot is not rendered at all (skipped in the slot
      // loop below) so its plane is never mounted/seeded.
      if (slotId === "top-left") return { gridColumn: "1", gridRow: "1 / 4" };
      if (slotId === companionSlotId) return { gridColumn: "3", gridRow: "1" };
      if (slotId === "bottom-right") return { gridColumn: "3", gridRow: "3" };
      return { display: "none" };
    }

    // Default 2x2 grid with gutter rows/columns (3x3 grid: content + gutter + content)
    const gridPos: Record<SlotId, React.CSSProperties> = {
      "top-left": { gridColumn: "1", gridRow: "1" },
      "top-right": { gridColumn: "3", gridRow: "1" },
      "bottom-left": { gridColumn: "1", gridRow: "3" },
      "bottom-right": { gridColumn: "3", gridRow: "3" },
    };
    return gridPos[slotId] ?? {};
  };

  // ─── Resizable grid splits ─────────────────────────────────────────────────
  // splitX: vertical divider position (0-1), splitY: horizontal divider position (0-1)
  // Clamped to 25%-75% range to prevent panels from disappearing.
  const [splitX, setSplitX] = useState(0.5);
  const [splitY, setSplitY] = useState(0.5);
  // seg-2up has its own splits so the "large axial" default (60/40) and the
  // right-column companion/3D split don't perturb the 2x2 50/50 defaults.
  const [segSplitX, setSegSplitX] = useState(0.6);
  const [segSplitY, setSegSplitY] = useState(0.5);
  const dragTypeRef = useRef<"x" | "y" | "both" | null>(null);

  const handleDragStart = useCallback(
    (
      type: "x" | "y" | "both",
      setX: (v: number) => void = setSplitX,
      setY: (v: number) => void = setSplitY,
    ) =>
      (e: React.MouseEvent) => {
        e.preventDefault();
        dragTypeRef.current = type;

        // Use requestAnimationFrame to throttle updates and reduce flicker
        let rafId = 0;
        const onMouseMove = (ev: MouseEvent) => {
          if (!gridRef.current || !dragTypeRef.current) return;
          cancelAnimationFrame(rafId);
          rafId = requestAnimationFrame(() => {
            if (!gridRef.current || !dragTypeRef.current) return;
            const rect = gridRef.current.getBoundingClientRect();
            const clamp = (v: number) => Math.max(0.25, Math.min(0.75, v));

            if (dragTypeRef.current === "x" || dragTypeRef.current === "both") {
              setX(clamp((ev.clientX - rect.left) / rect.width));
            }
            if (dragTypeRef.current === "y" || dragTypeRef.current === "both") {
              setY(clamp((ev.clientY - rect.top) / rect.height));
            }
          });
        };

        const onMouseUp = () => {
          dragTypeRef.current = null;
          document.removeEventListener("mousemove", onMouseMove);
          document.removeEventListener("mouseup", onMouseUp);
          document.body.style.cursor = "";
          document.body.style.userSelect = "";
        };

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
        document.body.style.cursor =
          type === "x" ? "col-resize" : type === "y" ? "row-resize" : "move";
        document.body.style.userSelect = "none";
      },
    [],
  );

  // Grid container style based on mode (focus > spotlight > layout > normal)
  const getGridStyle = (): React.CSSProperties => {
    if (isFocusMode) {
      return {
        display: "grid",
        gridTemplateColumns: "1fr",
        gridTemplateRows: "1fr",
        gap: "0px",
      };
    }

    if (isSpotlight) {
      return {
        display: "grid",
        gridTemplateColumns: "3fr 1fr",
        gridTemplateRows: "1fr 1fr 1fr",
        gap: "2px",
      };
    }

    if (layoutMode === "single") {
      return {
        display: "grid",
        gridTemplateColumns: "1fr",
        gridTemplateRows: "1fr",
        gap: "0px",
      };
    }

    if (layoutMode === "2x1") {
      return {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gridTemplateRows: "1fr",
        gap: "2px",
      };
    }

    if (isSeg2Up) {
      // Large axial left column | 4px | right column (companion over 3D),
      // split by segSplitY. 3×3 track model (content + gutter + content); the
      // left column spans all three row tracks via gridRow "1 / 4".
      const colLeft = `${(segSplitX * 100).toFixed(2)}%`;
      const colRight = `${((1 - segSplitX) * 100).toFixed(2)}%`;
      const rowTop = `${(segSplitY * 100).toFixed(2)}%`;
      const rowBottom = `${((1 - segSplitY) * 100).toFixed(2)}%`;
      return {
        display: "grid",
        gridTemplateColumns: `${colLeft} 4px ${colRight}`,
        gridTemplateRows: `${rowTop} 4px ${rowBottom}`,
        gap: "0px",
      };
    }

    // Default 2x2 with resizable splits
    const colLeft = `${(splitX * 100).toFixed(2)}%`;
    const colRight = `${((1 - splitX) * 100).toFixed(2)}%`;
    const rowTop = `${(splitY * 100).toFixed(2)}%`;
    const rowBottom = `${((1 - splitY) * 100).toFixed(2)}%`;
    return {
      display: "grid",
      gridTemplateColumns: `${colLeft} 4px ${colRight}`,
      gridTemplateRows: `${rowTop} 4px ${rowBottom}`,
      gap: "0px",
    };
  };

  const gridStyle = getGridStyle();

  // Share the single layout-scoped workspace with every pane. Flag OFF →
  // `sharedWorkspace` is null and consumers fall back to per-pane behavior,
  // leaving grid behavior exactly as today.
  return (
    <ViewportWorkspaceContext.Provider value={sharedWorkspace}>
      <div ref={gridCallbackRef} className="relative w-full h-full bg-surface-2" style={gridStyle}>
        {/* Drag handles for resizable splits (only in 2x2 mode) */}
        {effectiveLayoutMode === "2x2" && !isFocusMode && !isSpotlight && (
          <>
            {/* Vertical divider (col-resize) */}
            <div
              style={{ gridColumn: "2", gridRow: "1 / 4", cursor: "col-resize" }}
              className="bg-border hover:bg-signal/50 transition-colors z-20"
              onMouseDown={handleDragStart("x")}
            />
            {/* Horizontal divider (row-resize) */}
            <div
              style={{ gridColumn: "1 / 4", gridRow: "2", cursor: "row-resize" }}
              className="bg-border hover:bg-signal/50 transition-colors z-20"
              onMouseDown={handleDragStart("y")}
            />
            {/* Center intersection (move both) */}
            <div
              style={{ gridColumn: "2", gridRow: "2", cursor: "move" }}
              className="bg-border-strong hover:bg-signal/70 transition-colors z-30"
              onMouseDown={handleDragStart("both")}
            />
          </>
        )}
        {/* Drag handles for seg-2up: full-height column divider + a horizontal
            divider confined to the right (companion / 3D) column. */}
        {isSeg2Up && !isFocusMode && !isSpotlight && (
          <>
            <div
              style={{ gridColumn: "2", gridRow: "1 / 4", cursor: "col-resize" }}
              className="bg-border hover:bg-signal/50 transition-colors z-20"
              onMouseDown={handleDragStart("x", setSegSplitX, setSegSplitY)}
            />
            <div
              style={{ gridColumn: "3", gridRow: "2", cursor: "row-resize" }}
              className="bg-border hover:bg-signal/50 transition-colors z-20"
              onMouseDown={handleDragStart("y", setSegSplitX, setSegSplitY)}
            />
          </>
        )}
        {SLOT_IDS.map((slotId) => {
          // seg-2up renders only axial + ONE companion + the 3D model. The
          // non-companion plane's slot is not mounted at all, so its plane is
          // never created in Cornerstone and never seeded by nnInteractive.
          if (isSeg2Up && slotId === hiddenCompanionSlotId) return null;
          const slotState = getSlotState(slotId);
          const resliceTarget = resliceBySlot[slotId];
          // A reslice slot's own `seriesId` is null (see SlotState.reslice) — a
          // reslice pane renders the SOURCE axial (shared cached volume) at a
          // target plane, so this fallback is genuinely load-bearing, not
          // redundant. Pass the source identity as seriesId so the pane's
          // series-derived state (viewportKind, metadata, no early-return)
          // resolves; the `reslice` marker then overrides orientation + plane
          // labels and disables segmentation.
          const seriesId = resliceTarget?.sourceSeriesId ?? slotState.seriesId;
          const slotSeriesMetadata = seriesId ? (seriesMetadata?.[seriesId] ?? null) : null;
          const defaultViewport = (
            <ProtocolViewport
              slotId={slotId}
              seriesId={seriesId}
              seriesMetadata={slotSeriesMetadata}
              studyMetadata={studyMetadata}
              studyId={studyId}
              onSeriesLoadComplete={handleSeriesLoadComplete}
              isSegmentationProject={isSegmentationProject}
              blindAnatomyTask={blindAnatomyTask}
              onBeforeSeriesDrop={handleBeforeSeriesDrop}
              onSeriesDrop={handleSeriesDrop}
              reslice={resliceTarget}
              unifiedPath={resliceUnified}
            />
          );
          const renderBottomRight = () => {
            if (showMeasure3D) return <MeasureSegmentationViewerPanel />;
            if (
              isSegmentationProject &&
              showSegmentationPanel &&
              renderBottomRightAsSegPanel(gatingMode, phaseCount, isAnnotationActive)
            ) {
              return <SegmentationViewerPanel />;
            }
            if (
              gatingMode === GATING_MODE.PHASE_DEPENDENT &&
              !isAnnotationActive &&
              phaseCount >= 5
            ) {
              return (
                <PhaseSummaryPanel
                  shownSeriesUids={
                    new Set(
                      [slotTopLeft.seriesId, slotTopRight.seriesId, slotBottomLeft.seriesId].filter(
                        (id): id is string => !!id,
                      ),
                    )
                  }
                />
              );
            }
            return defaultViewport;
          };
          return (
            <div
              key={slotId}
              data-slot-id={slotId}
              className="relative overflow-hidden"
              style={{ ...getSlotStyle(slotId), contain: "strict" }}
            >
              {slotId === "bottom-right" ? renderBottomRight() : defaultViewport}
              {isSeg2Up && slotId === companionSlotId && (
                <>
                  {/* Plane color-code (matches the 3D legend): a colored inset
                      ring marks the companion as coronal (green) or sagittal
                      (red). Inline style avoids Tailwind purging dynamic hexes.
                      `66` ≈ 40% alpha in #rrggbbaa. */}
                  <div
                    className="pointer-events-none absolute inset-0 z-20"
                    style={{
                      boxShadow: `inset 0 0 0 2px ${
                        PLANE_COLORS_HEX[COMPANION_TO_PLANE_KEY[companionPlane]]
                      }66`,
                    }}
                  />
                  <CompanionPlaneToggle plane={companionPlane} onChange={setCompanionPlane} />
                </>
              )}
            </div>
          );
        })}

        {/* First-time keyboard shortcuts hint */}
        <FirstTimeHelpToast />

        {/* Acquisition-mismatch warning is now a soft toast (viewerBus →
            PageToast) emitted from handleBeforeSeriesDrop — no blocking dialog. */}
      </div>
    </ViewportWorkspaceContext.Provider>
  );
};

export default HangingProtocolGrid;
